import Image from "next/image";
import React, { useCallback, useEffect, useState } from "react";
import lightBulb from "@/assets/icons/lightBulb.svg";
import {
    fetchTradeCardById,
    getInstrument,
    isFutures,
    isFuturesOrOptions,
    isOptions,
    termColor,
    termText
} from "@/lib/tradeCards";
import { FaClock } from "react-icons/fa";
import { useSubscription } from "react-stomp-hooks";
import Actions from "@/elements/Actions";
import downloadIcon from "@/assets/icons/PDF_icon.png";

import { BsInfoCircle } from "react-icons/bs";

import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { cn, putComma } from "@/lib/utils";
import { Inter } from "next/font/google";
import { Mixpanel } from "@/elements/Mixpanel";
import { Button } from "../ui/button";
import { useRouter } from "next/router";
import { TradeCardBody } from "../PaidUsers/TradeCards/LiveTradeIdeaCardV2";
import { ClosedTradeIdeaCardBody } from "../PaidUsers/TradeCards/ClosedTradeIdeaCard";

const inter = Inter({
    subsets: ["latin"],
    variable: "--font-inter"
});

export default function TradeCardBF({ tradeId, children, stockDetails, pageName }) {
    const router = useRouter();
    const [open, setOpen] = useState(false);

    const [stockPrices, setStockPrices] = useState({});

    const [ele, setEle] = useState(null);

    useEffect(() => {
        if (tradeId && open) {
            fetchTradeCardById(tradeId).then((res) => {
                setEle(res);
            });
        }
    }, [tradeId, open]);

    const calcNetGainLoss = useCallback(
        (ele) => {
            const price = stockPrices?.[ele?.finCode] ?? ele?.ltpPrice;
            if (price && ele?.suggestedPrice) {
                let final = 0.0;
                if (ele?.term === "FUTURES") {
                    final = (price - ele?.suggestedPrice) * ele?.lotSize;
                    if (ele?.recommendationType === "SELL") {
                        final *= -1;
                    }
                    final += ele?.adjustedGain ?? 0;
                } else if (isOptions(ele?.term)) {
                    let total = 0.0;
                    if (ele?.tradeCardOptionsDataList) {
                        for (let tradeOption of ele?.tradeCardOptionsDataList) {
                            const optionPrice = stockPrices?.[tradeOption?.finCode] ?? tradeOption?.nseLtpPrice;
                            if (optionPrice) {
                                total +=
                                    tradeOption.ratio *
                                    (optionPrice - tradeOption.suggestedPrice) *
                                    tradeOption.lotSize *
                                    (tradeOption.recommendationType === "BUY" ? -1 : 1);
                            }
                        }
                    }
                    final = total * -1;
                } else {
                    final = ((price - ele?.suggestedPrice + (ele?.adjustedGain || 0)) / ele?.suggestedPrice) * 100;
                }
                return parseFloat(final.toFixed(2));
            }
            return 0.0;
        },
        [stockPrices]
    );

    const calcNetPriceChange = useCallback(
        (ele) => {
            const price = stockPrices?.[ele?.finCode] ?? ele?.ltpPrice;
            const closePrice = ele?.closePrice;
            if (price && closePrice) {
                const change = price - closePrice;
                const percentageChange = (change / closePrice) * 100;
                return {
                    change: parseFloat(change.toFixed(2)),
                    percentageChange: parseFloat(percentageChange.toFixed(2))
                };
            }
            return { change: 0.0, percentageChange: 0.0 };
        },
        [stockPrices]
    );

    const calcPotential = useCallback(
        (ele) => {
            const price = stockPrices?.[ele?.finCode] ?? ele?.ltpPrice;
            if (price && ele?.targetPrice) {
                let final = 0.0;
                if (ele.term === "FUTURES") {
                    final = (ele?.targetPrice - price) * ele?.lotSize;
                    if (ele.recommendationType === "SELL") {
                        final *= -1;
                    }
                } else if (isOptions(ele?.term)) {
                    let netGain = calcNetGainLoss(ele);
                    if (netGain) {
                        final = ele?.targetPrice - netGain;
                    }
                } else {
                    final = ((ele?.targetPrice - price) / price) * 100;
                }
                return parseFloat(final.toFixed(2));
            }
            return 0.0;
        },
        [stockPrices]
    );

    // useSubscription(`/stock/live-price/${ele?.finCode}`, (res) => {
    //     const newPrice = JSON.parse(res.body).ltpPrice;
    //     setStockPrices(prevPrices => ({
    //         ...prevPrices,
    //         [ele?.finCode]: newPrice
    //     }));
    // });

    const handleDownload = () => {
        const attachment = JSON.parse(ele?.attachments)?.attachments[0];
        if (attachment?.url) {
            const link = document.createElement("a");
            link.href = attachment.url;
            link.target = "_blank";
            link.rel = "noopener noreferrer"; // For security reasons
            link.click();
        }
    };

    useEffect(() => {
        if (open && ele) {
            Mixpanel.track("cta_clicked", {
                page: pageName,
                cta_text: "view_trade",
                symbol: stockDetails?.nseSymbol ?? stockDetails?.bseSymbol ?? stockDetails?.compName,
                expiry_date: isFuturesOrOptions(ele?.term) ? ele?.expectedDurationEnum : null,
                instrument_name: getInstrument(stockDetails?.nseSymbol ?? stockDetails?.bseSymbol),
                trade_id: tradeId,
                term: ele?.term
            });
        }
    }, [open]);

    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
                {children ? (
                    children
                ) : (
                    <div className="text-xs font-semibold text-primary/80 flex items-center gap-1 cursor-pointer">
                        <Image src={lightBulb} alt="lightBulb" className="w-4 object-contain" />
                        View trade
                    </div>
                )}
            </DropdownMenuTrigger>
            <DropdownMenuContent
                className={cn("w-[444px] mr-10 rounded-2xl p-0 font-Inter shadow-2xl", inter.variable)}
            >
                {ele ? (
                    <div className="flex flex-col justify-center items-center gap-4 bg-[#0c1724] p-3">
                        <div className=" trade-card gap-3 bg-[#C4E1F4] p-3 rounded-lg">
                            {ele?.open ? (
                                <TradeCardBody
                                    ele={ele}
                                    calcNetPriceChange={calcNetPriceChange}
                                    stockPrices={stockPrices}
                                    setStockPrices={setStockPrices}
                                    calcPotential={calcPotential}
                                    calcNetGainLoss={calcNetGainLoss}
                                    onBf={false}
                                    className="w-[396px]"
                                />
                            ) : (
                                <ClosedTradeIdeaCardBody
                                    onBf={false}
                                    className="w-[396px]"
                                    ele={ele}
                                    getClosedTradeCard={() => {}}
                                />
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="w-full h-[288px] bg-slate-100 animate-pulse rounded-2xl flex justify-center">
                        <div className="self-center h-10 w-10 border-e-4 lg:h-20 lg:w-20 lg:border-e-8 border-[#00439D] animate-spin rounded-full" />
                    </div>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export function TradeOptionBottomSheet({ lastKey, optionDetails, stockPrices, setStockPrices }) {
    // useSubscription(`/stock/live-price/${optionDetails?.finCode}`, (res) => {
    //     const newPrice = JSON.parse(res.body).ltpPrice;
    //     setStockPrices(prevPrices => ({
    //         ...prevPrices,
    //         [optionDetails?.finCode]: newPrice
    //     }));
    // });
    return (
        <div
            className={` w-full flex items-center gap-8 self-stretch px-3 py-0 ${
                !lastKey && " border-b-[1px] border-[#DFDFDF] pb-2"
            }`}
        >
            <div className="flex items-center gap-2">
                <p
                    className={`flex justify-center items-center rounded border ${
                        optionDetails?.recommendationType === "BUY"
                            ? "border-[#148F36] text-[#148F36] "
                            : "border-[#EB4E2C] text-[#EB4E2C]"
                    }  px-[5px] py-0.5 border-solid  text-center text-[8px] font-bold leading-3 `}
                >
                    {optionDetails?.recommendationType === "BUY" ? "Buy" : "Sell"}
                </p>
                <p className=" text-[#0862BC] text-[10px] font-bold leading-4 underline">{optionDetails?.symbol}</p>
            </div>
            <div className="flex justify-between gap-4 items-center flex-[1_0_0]">
                {/* <p className='text-[color:var(--Neutral-900,#202020)] text-right text-[10px] font-semibold leading-4' >{putComma(optionDetails?.ratio ?? '0')}</p> */}
                <p className="text-black text-right text-[10px] font-semibold leading-4">
                    ₹{putComma(optionDetails?.suggestedPrice ?? "0.00")}
                </p>
                <p className="text-black text-right text-[10px] font-semibold leading-4">
                    ₹{putComma(stockPrices?.[optionDetails?.finCode] || (optionDetails?.nseLtpPrice ?? "0.00"))}
                </p>
            </div>
        </div>
    );
}
